import React, { useState, useRef, useEffect } from 'react';
import { Mail, Lock, Eye, EyeOff, User, ArrowRight, ArrowLeft, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GlassCard } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import veriLinkLogo from '@/assets/verilink-logo.png';

export const AuthPage = ({ onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [shaking, setShaking] = useState(false);

  const passwordRef = useRef(null);
  const { login, register } = useAuth();

  useEffect(() => {
    if (hasSubmitted && error) {
      setShaking(true);
      const t = setTimeout(() => setShaking(false), 500);
      return () => clearTimeout(t);
    }
  }, [error, hasSubmitted]);

  const handleEmailChange = (e) => { setEmail(e.target.value); if (error) setError(''); };
  const handlePasswordChange = (e) => { setPassword(e.target.value); if (error) setError(''); };
  const handleNameChange = (e) => { setName(e.target.value); if (error) setError(''); };

  useEffect(() => {
    if (hasSubmitted && error && isLogin && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [error, hasSubmitted, isLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setError('');
    setHasSubmitted(true);
    setIsLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
      } else {
        if (password.length < 8) {
          throw new Error('Password must be at least 8 characters');
        }
        if (!/\d/.test(password)) {
          throw new Error('Password must contain at least one number');
        }
        result = await register(email, password, name);
      }

      if (result.success) {
        onClose?.();
      } else {
        setError(result.error || 'Authentication failed. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setHasSubmitted(false);
  };

  const showError = hasSubmitted && error;
  const inputErrorClass = showError ? 'border-destructive focus-visible:ring-destructive/30' : '';

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className={cn("w-full max-w-md", shaking && "animate-[shake_0.5s_ease-in-out]")}>
        <style>{`@keyframes shake{0%,100%{transform:translateX(0)}10%,50%,90%{transform:translateX(-6px)}30%,70%{transform:translateX(6px)}}`}</style>

        <button
          type="button"
          onClick={onClose}
          data-testid="auth-back-btn"
          className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to VeriLink
        </button>

        <GlassCard className="p-6 sm:p-8">
          <div className="flex flex-col items-center mb-6">
            <img src={veriLinkLogo} alt="VeriLink Logo" className="w-16 h-16 rounded-xl mb-3" />
            <h1 className="font-display text-2xl font-bold text-foreground">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isLogin ? 'Sign in to access your verification history' : 'Join VeriLink to save your verifications'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Name</label>
                <div className="relative">
                  <Input type="text" placeholder="Your name" value={name} onChange={handleNameChange}
                    className="h-12 pl-10" data-testid="auth-name-input" />
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Email</label>
              <div className="relative">
                <Input type="email" placeholder="you@example.com" value={email} onChange={handleEmailChange}
                  required className={cn("h-12 pl-10", inputErrorClass)} data-testid="auth-email-input" />
                <Mail className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4",
                  showError ? "text-destructive" : "text-muted-foreground")} />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Password</label>
              <div className="relative">
                <Input ref={passwordRef}
                  type={showPassword ? 'text' : 'password'}
                  placeholder={isLogin ? 'Your password' : 'Min 8 chars with a number'}
                  value={password} onChange={handlePasswordChange}
                  required className={cn("h-12 pl-10 pr-10", inputErrorClass)} data-testid="auth-password-input" />
                <Lock className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4",
                  showError ? "text-destructive" : "text-muted-foreground")} />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {showError && (
              <div
                className="p-3 rounded-lg bg-destructive/10 border border-destructive/30 flex items-start gap-2 animate-[fadeIn_0.2s_ease-out]"
                data-testid="auth-error-message"
              >
                <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}`}</style>
                <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <Button type="submit" disabled={isLoading} className="w-full h-12 text-base" variant="gradient" data-testid="auth-submit-btn">
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button type="button" onClick={toggleMode}
                className="ml-1 text-primary hover:underline font-medium" data-testid="auth-toggle-mode">
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default AuthPage;
